import { useI18n } from '~/common/context';
import { Flex, Icon, IconButton, useConfirmation } from '@wyz/ui';
import * as React from 'react';
import { Link } from 'react-router';

type EditAction =
  | {
      type: 'BUTTON';
      handler: () => void;
    }
  | {
      type: 'LINK';
      to: string;
    };
type DetailAction =
  | {
      type: 'BUTTON';
      handler: () => void;
    }
  | {
      type: 'LINK';
      to: string;
    };

type DeleteAction = {
  handler: () => void;
  doConfirm?: boolean;
};

type BaseProps = {
  rowIdentifier: string;
  edit?: EditAction;
  remove?: DeleteAction; // using remove because delete is a js keyword
  detail?: DetailAction;
};

type DataTableActionsProps = BaseProps;

function EditLink({ to, id }: { to: string; id: string }) {
  return (
    <Link className='text-neutral-grey-6' to={to} data-cy={`edit-${id}`}>
      <Icon id={`edit-${id}`} icon='pencil-line' size='xs' />
    </Link>
  );
}
function EditButton({ id, handler }: { id: string; handler: () => void }) {
  return (
    <IconButton
      id={`edit-${id}`}
      icon='pencil-line'
      variant='link'
      size='xs'
      iconSize='xs'
      onClick={handler}
    ></IconButton>
  );
}

function Delete({
  handler,
  id,
  doConfirm = true,
}: {
  handler: () => void;
  id: string;
  doConfirm: boolean;
}) {
  const { t } = useI18n();
  const ask = useConfirmation();
  return (
    <>
      <IconButton
        id={`delete-${id}`}
        variant='link'
        size='xs'
        iconSize='xs'
        icon='trash'
        onClick={() =>
          doConfirm
            ? ask({
                title: t('app.list.delete_modal.title'),
                description: t('app.common.form.message_confirmation'),
                cancelBtnText: t('app.common.button.cancel'),
                confirmBtnText: t('app.common.button.confirm'),
                confirmBtnVariant: 'danger',
              }).onConfirm(handler)
            : handler()
        }
      ></IconButton>
    </>
  );
}

function ViewDetailModal({ handler, id }: { handler: () => void; id: string }) {
  return (
    <IconButton
      id={`detail-${id}`}
      icon='eye'
      variant='link'
      size='xs'
      iconSize='xs'
      onClick={handler}
    />
  );
}

function ViewDetailPage({ to, id }: { to: string; id: string }) {
  return (
    <Link className='text-neutral-grey-6' to={to} data-cy={`detail-${id}`}>
      <Icon id={`detail-${id}`} size='xs' icon='eye' />
    </Link>
  );
}

export default function DataTableActions({
  rowIdentifier,
  remove,
  detail,
  edit,
  children,
}: React.PropsWithChildren<DataTableActionsProps>) {
  if (!remove && !edit && !detail && !children) {
    return null;
  }

  return (
    <Flex className='actions d-flex align-items-center space-x-2 me-2' inline>
      {edit && (
        <>
          {edit.type === 'LINK' && <EditLink id={rowIdentifier} to={edit.to} />}
          {edit.type === 'BUTTON' && (
            <EditButton id={rowIdentifier} handler={edit.handler} />
          )}
        </>
      )}
      {remove && (
        <Delete
          id={rowIdentifier}
          handler={remove.handler}
          doConfirm={Boolean(remove.doConfirm)}
        />
      )}
      {detail && (
        <>
          {detail.type === 'BUTTON' && (
            <ViewDetailModal id={rowIdentifier} handler={detail.handler} />
          )}
          {detail.type === 'LINK' && (
            <ViewDetailPage id={rowIdentifier} to={detail.to} />
          )}
        </>
      )}
      {children}
    </Flex>
  );
}
